import { Trans, useLingui } from '@lingui/react/macro';
import { Download, FolderInput, Pencil, Trash2 } from 'lucide-react';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { mediaKindLabel } from 'modules/manager/domain/media-kind';
import React, { useEffect, useRef } from 'react';
import { theme } from 'styles/theme/theme';
import { UDButton, UDText } from 'ud-ui/components';
import EmptyButton from 'ud-ui/components/empty-button';
import UDCard from 'ud-ui/components/card';
import UDTippy from 'ud-ui/components/tippy';
import MediaManagerPresetPreview from './media-manager-preset-preview';
import { PRESET_CARD_ACTIONS_GAP, PRESET_CARD_LAYOUT_CLASS, PRESET_CARD_PROPS } from './preset-card-metrics';

interface IProps {
  template: MediaManagerTemplate;
  /** Flash the card and scroll it into view — used right after a duplicate. */
  isHighlighted?: boolean;
  onClick: (template: MediaManagerTemplate) => void;
  onEdit: (template: MediaManagerTemplate) => void;
  onDelete: (template: MediaManagerTemplate) => void;
  /** Возвращает промис выгрузки: кнопка экспорта заперта до её конца (IMGM-1561). */
  onExport: (template: MediaManagerTemplate) => Promise<unknown>;
  onMove?: (template: MediaManagerTemplate) => void;
  /** An export is running — the download control stays blocked until it lands. */
  isExporting?: boolean;
}

/**
 * Шаблон организации карточкой — той же раскладки, что `MediaManagerPresetCard`: превью, имя,
 * тип медиа и ряд «кнопка + иконки». Набор действий прежний (переместить, скачать, редактировать,
 * удалить; открытие — по имени и по кнопке), меняется только оформление.
 */
function MediaManagerTemplateListElement(props: IProps) {
  const { template, isHighlighted, isExporting, onClick, onEdit, onDelete, onExport, onMove } = props;
  const { t } = useLingui();
  const rootRef = useRef<HTMLElement>(null);
  const typeLabel = t(mediaKindLabel(template.mediaKind));

  useEffect(() => {
    if (!isHighlighted) return;
    rootRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [isHighlighted]);

  return (
    <UDCard
      ref={rootRef}
      {...PRESET_CARD_PROPS}
      // Рамку и фон подсветки выбирает кит: два `border-*`/`bg-*` в `className` решил бы порядок CSS.
      isHighlighted={Boolean(isHighlighted)}
      className={PRESET_CARD_LAYOUT_CLASS}
    >
      <MediaManagerPresetPreview template={template} />

      <div className="flex min-w-0 flex-col">
        <button type="button" className="min-w-0 cursor-pointer text-left" onClick={() => onClick(template)}>
          <UDText variant="body-2" className="truncate font-bold">
            {template.name}
          </UDText>
        </button>
        <UDText variant="body-3" className="text-gray-400">
          {typeLabel}
        </UDText>
      </div>

      {/* Кнопка + четыре иконки не помещаются в узкую карточку (три колонки на ~1000px): ряд переносится, а не обрезает корзину. */}
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-3">
        <UDButton variant="primary" onClick={() => onClick(template)}>
          <Trans>Открыть</Trans>
        </UDButton>
        <div className="flex items-center" style={{ gap: PRESET_CARD_ACTIONS_GAP }}>
          {onMove && (
            <UDTippy content={t`Переместить в группу`} placement={'top'}>
              <EmptyButton icon={<FolderInput size={20} />} aria-label={t`Переместить в группу`} onClick={() => onMove(template)} />
            </UDTippy>
          )}
          <UDTippy content={t`Скачать файл`} placement={'top'}>
            <EmptyButton
              icon={<Download size={20} />}
              disabled={isExporting}
              aria-label={t`Скачать файл`}
              onClick={() => onExport(template)}
            />
          </UDTippy>
          <UDTippy content={t`Редактировать`} placement={'top'}>
            <EmptyButton icon={<Pencil size={20} />} onClick={() => onEdit(template)} />
          </UDTippy>
          <UDTippy content={t`Удалить`} placement={'top'}>
            <EmptyButton color={theme.colors.error} icon={<Trash2 size={20} />} onClick={() => onDelete(template)} />
          </UDTippy>
        </div>
      </div>
    </UDCard>
  );
}

export default MediaManagerTemplateListElement;
